import dayjs from 'dayjs';
import { Task } from '@medplum/fhirtypes';
import { createReference, getReferenceString } from '@medplum/core';
import styles from './TasksManagement.module.css';
import { CreateTaskForm } from './CreateTaskForm';
import { EditTaskForm } from './EditTaskForm';
import {
  canCompleteClinicalTask,
  describeClinicalTaskDue,
  getClinicalTaskAssignee,
  getClinicalTaskDueDate,
  partitionClinicalTasks,
} from '@bariendo/lib/clinical-tasks';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { generateHTML } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import { createRichTextExtensions } from '@/components/RichTextInput/richTextExtensions';
import { modals } from '@mantine/modals';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { alert } from '@/services/alert';
import { getClinicalTaskPerformerRole } from '@/services/task-management';
import { RoleAvatar, TeammateAvatar } from '@/components/TeamAvatar';
import { useTaskCompletion } from '@/hooks/useTaskCompletion';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { IconPlus, IconFlagFilled, IconCheck, IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { Stack, Text, ActionIcon, Flex, Group, UnstyledButton, Box, Tooltip, Menu } from '@mantine/core';

const COMPLETE_BUTTON_COLOR = 'var(--mantine-color-gray-6)';

/**
 * Read-only render of a task's description. Rich-text JSON renders as HTML
 * via the same tiptap extensions the editor uses (HighlightableRichText
 * precedent); plain text renders verbatim.
 */
function TaskDescriptionBody({ note }: { note: string }): ReactElement {
  const html = useMemo(() => {
    try {
      const parsed: unknown = JSON.parse(note);
      if (parsed && typeof parsed === 'object') {
        return generateHTML(parsed as JSONContent, createRichTextExtensions({ mentionItems: [] }));
      }
    } catch {
      // Plain-text note — rendered below without HTML.
    }
    return undefined;
  }, [note]);

  if (!html) {
    return (
      <Text size="sm" c="dimmed" mt={4}>
        {note}
      </Text>
    );
  }
  return (
    <Text
      size="sm"
      c="dimmed"
      mt={4}
      component="div"
      className={styles.taskDescriptionBody}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

interface TaskCardProps {
  task: Task;
  onComplete: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  /** Disables every mutating action on the row while a task write is in flight. */
  actionsDisabled: boolean;
  /** Whether the CURRENT USER may complete this task (assignee or matching role — see canCompleteClinicalTask). */
  canComplete: boolean;
}

function TaskCard({ task, onComplete, onEdit, onDelete, actionsDisabled, canComplete }: TaskCardProps): ReactElement {
  const [isHovered, setIsHovered] = useState(false);

  const dueDate = getClinicalTaskDueDate(task);
  const due = useMemo(() => (dueDate ? describeClinicalTaskDue(dueDate) : undefined), [dueDate]);
  const isOverdue = due?.overdue ?? false;
  const isHighPriority = task.priority === 'urgent';
  const noteText = task.note?.[0]?.text;

  const assignee = getClinicalTaskAssignee(task);
  const performerRole = useMemo(() => getClinicalTaskPerformerRole(task), [task]);

  const renderActions = () => (
    <>
      {isHovered ? (
        <Menu shadow="md" width="fit-content" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="xs" c="var(--mantine-color-gray-5)">
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item disabled={actionsDisabled} onClick={() => onEdit(task)} leftSection={<IconEdit size={12} />}>
              Edit
            </Menu.Item>
            <Menu.Item
              disabled={actionsDisabled}
              onClick={() => onDelete(task)}
              c="var(--mantine-color-error)"
              leftSection={<IconTrash size={12} />}
            >
              Delete task
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        due &&
        dueDate && (
          <Text
            component="time"
            size="xs"
            style={{ whiteSpace: 'nowrap' }}
            title={dayjs(dueDate).format('MMMM D, YYYY')}
            c={due.overdue ? 'var(--mantine-color-error)' : 'var(--mantine-color-gray-5)'}
          >
            {due.label}
          </Text>
        )
      )}
    </>
  );

  return (
    <Group
      p="xs"
      gap="xs"
      align="start"
      wrap="nowrap"
      justify="space-between"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      bg={isOverdue ? 'var(--mantine-color-red-0)' : ''}
      style={{ borderRadius: 'var(--mantine-radius-md)' }}
    >
      <Box style={{ flex: '10%' }} ta="center">
        <Tooltip
          label={canComplete ? 'Complete task' : 'Only the assigned owner or role can complete this task'}
          disabled={!isHovered}
        >
          <UnstyledButton
            h={30}
            w={30}
            fz="xs"
            ta="center"
            aria-label="Complete task"
            disabled={actionsDisabled || !canComplete}
            onClick={() => canComplete && onComplete(task)}
            className={styles.completeTaskButton}
            c={isHovered && canComplete ? COMPLETE_BUTTON_COLOR : undefined}
            bg={isHovered && canComplete ? 'white' : performerRole ? 'transparent' : 'var(--mantine-color-gray-1)'}
            bd={isHovered && canComplete ? `1px solid ${COMPLETE_BUTTON_COLOR}` : '1px solid transparent'}
          >
            {isHovered && canComplete ? (
              <IconCheck size={14} color={COMPLETE_BUTTON_COLOR} />
            ) : assignee ? (
              <TeammateAvatar practitioner={assignee} size={22} />
            ) : (
              <RoleAvatar role={performerRole} size={28} />
            )}
          </UnstyledButton>
        </Tooltip>
      </Box>

      <Box style={{ flex: '65%' }}>
        <Group gap={6} wrap="nowrap" align="center">
          {/* Red flag marks HIGH PRIORITY, leading the title so it reads apart from the overdue date. */}
          {isHighPriority && <IconFlagFilled size={10} color="var(--mantine-color-error)" style={{ flexShrink: 0 }} />}
          <Text>{task.description}</Text>
        </Group>
        {noteText && <TaskDescriptionBody note={noteText} />}
      </Box>

      <Box style={{ flex: '25%' }} ta="right">
        {renderActions()}
      </Box>
    </Group>
  );
}

export interface TasksManagementProps {
  /** Task data lifted to PatientInfoPanel (shared with the tab's status dot). */
  tasks: Task[];
  loading: boolean;
  refresh: () => Promise<void>;
}

export function TasksManagement({ tasks, loading, refresh }: TasksManagementProps): ReactElement {
  const { patientContext, timelineContext } = useTimelineContext();
  const [showCreateForm, setShowCreateForm] = useState(false);
  // SNAPSHOT of the task as the user saw it when they clicked Edit — not a
  // live row. The edit patch must test against THIS version: if the form
  // tracked the subscription-refreshed row instead, a concurrent change
  // would slip past the versionId guard and the save would silently revert
  // fields the other actor changed (lost update).
  const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);

  const patient = patientContext?.patient;
  const {
    currentUser,
    currentUserRole,
    currentUserPermissions: { isAdmin, hasAdminMembership },
  } = useCurrentUser();
  const currentUserRef = currentUser ? getReferenceString(currentUser) : undefined;
  const isAdminUser = isAdmin || hasAdminMembership;
  // Refresh the task list AND the timeline: completing a task adds a
  // "Task completed" row, and reopening removes one. Same composite refresh
  // other cards use after a mutation (EncounterTimelineItem precedent).
  const handleTaskChange = useCallback(async () => {
    await refresh();
    timelineContext.refresh();
  }, [refresh, timelineContext]);

  const { completeTask, deleteTask, isUpdating } = useTaskCompletion(handleTaskChange);

  const confirmDelete = useCallback(
    (task: Task) => {
      modals.openConfirmModal({
        title: 'Delete task?',
        children: <Text size="sm">{task.description}</Text>,
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => void deleteTask(task),
      });
    },
    [deleteTask]
  );

  const { open: openTasks, overdue: overdueTasks } = useMemo(() => partitionClinicalTasks(tasks), [tasks]);

  // Rendered either in place of the task's row, or standalone when the task
  // has left the open list mid-edit (completed/deleted elsewhere).
  const renderEditForm = (): ReactElement | null =>
    editingTask ? (
      <EditTaskForm
        key={editingTask.id}
        task={editingTask}
        onChange={refresh}
        onCancel={() => setEditingTask(undefined)}
        onSave={(updated) => {
          setEditingTask(undefined);
          alert.success({ title: 'Task updated', message: updated.description ?? '' });
        }}
      />
    ) : null;

  const renderSummary = () => {
    if (openTasks.length === 0 && overdueTasks.length === 0) {
      return (
        <Text size="xs" c="var(--mantine-color-gray-5)">
          {loading ? 'Loading tasks…' : 'No tasks'}
        </Text>
      );
    }

    if (openTasks.length > 0 && overdueTasks.length === 0) {
      return (
        <Text size="xs" c="var(--mantine-color-gray-5)">
          {openTasks.length} open
        </Text>
      );
    }

    if (openTasks.length === 0 && overdueTasks.length > 0) {
      return (
        <Text size="xs" c="var(--mantine-color-error)">
          {overdueTasks.length} overdue
        </Text>
      );
    }

    return (
      <Text size="xs" c="var(--mantine-color-gray-5)">
        {openTasks.length} open ·{' '}
        <Text size="xs" component="span" c="var(--mantine-color-error)">
          {overdueTasks.length} overdue
        </Text>
      </Text>
    );
  };

  return (
    <Stack gap="xs">
      <Flex
        justify="space-between"
        align="center"
        pb="md"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
      >
        {renderSummary()}
        <ActionIcon
          size="xs"
          variant="subtle"
          disabled={!patient}
          aria-label="Create task"
          c="var(--mantine-color-gray-5)"
          onClick={() => setShowCreateForm(true)}
        >
          <IconPlus />
        </ActionIcon>
      </Flex>

      {showCreateForm && patient && (
        <CreateTaskForm
          patient={createReference(patient)}
          onCancel={() => setShowCreateForm(false)}
          onSave={(task) => {
            setShowCreateForm(false);
            alert.success({ title: 'Task created', message: task.description ?? '' });
            refresh();
          }}
        />
      )}

      {tasks.map((task) =>
        editingTask && task.id === editingTask.id ? (
          renderEditForm()
        ) : (
          <TaskCard
            key={task.id}
            task={task}
            onComplete={completeTask}
            onEdit={setEditingTask}
            onDelete={confirmDelete}
            actionsDisabled={isUpdating}
            canComplete={canCompleteClinicalTask(task, {
              profileRef: currentUserRef,
              role: currentUserRole,
              isAdmin: isAdminUser,
            })}
          />
        )
      )}

      {/* Keep the form alive if the task left the open list mid-edit (completed
          or deleted elsewhere) — unmounting would discard the user's typing.
          Saving then resolves against the version guard like any other edit. */}
      {editingTask && !tasks.some((task) => task.id === editingTask.id) && renderEditForm()}
    </Stack>
  );
}
